import { useState, useContext } from 'react';
import { Box, Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import SuperAdminService from '../../../../shared/services/super-admin/super-admin.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

export const SuperAdminTransactionsPage = () => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const superAdminService = new SuperAdminService();

  const [organisationId, setOrganisationId] = useState<string>('');
  const [calendarYear, setCalendarYear] = useState<string>('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [includeSpendBased, setIncludeSpendBased] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async () => {
    // Validate that at least one field is provided
    if (!organisationId && !calendarYear) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'Please provide either an Organisation ID or Calendar Year',
        backgroundColor: 'text.primary'
      });
      return;
    }

    setIsSubmitting(true);

    const payload: { organisationId?: string; calendarYear?: number; scopes?: string[]; includeSpendBased?: boolean } = {};
    if (organisationId) {
      payload.organisationId = organisationId;
    }
    if (calendarYear) {
      payload.calendarYear = parseInt(calendarYear, 10);
    }
    if (selectedScopes && selectedScopes.length > 0) {
      payload.scopes = selectedScopes;
    }
    if (includeSpendBased) {
      payload.includeSpendBased = true;
    }

    try {
      const response = await superAdminService.regenerateTransactions(payload);
      setIsSubmitting(false);
      userNotificationsContext.openSnackbar({
        open: true,
        message: `Successfully regenerated ${response.countRegenerated} transaction(s)`,
        backgroundColor: 'primary.dark'
      });
      // Clear form
      setOrganisationId('');
      setCalendarYear('');
      setSelectedScopes([]);
      setIncludeSpendBased(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'There was an error regenerating transactions, please try again',
        backgroundColor: 'text.primary'
      });
    }
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title='Customer Transactions'
        includeBackButton={true}
        backButtonLink='/super-admin'
        backButtonText='Back to super admin'
      />

      <Box sx={{ mb: 4 }}>
        <Typography variant='h6' sx={{ mb: 3 }}>
          Regenerate transactions
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label='Organisation ID'
              value={organisationId}
              onChange={(e) => setOrganisationId(e.target.value)}
              disabled={isSubmitting}
              placeholder='Enter organisation ID'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label='Calendar Year'
              type='number'
              value={calendarYear}
              onChange={(e) => setCalendarYear(e.target.value)}
              disabled={isSubmitting}
              placeholder='Enter calendar year (e.g., 2024)'
              inputProps={{ min: 2000, max: 2100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth disabled={isSubmitting}>
              <InputLabel id='scopes-label'>Scopes (optional: Default 1 + 2)</InputLabel>
              <Select
                labelId='scopes-label'
                multiple
                value={selectedScopes}
                label='Scopes (optional: Default 1 + 2)'
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedScopes(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                <MenuItem value='1'>1</MenuItem>
                <MenuItem value='2'>2</MenuItem>
                <MenuItem value='3'>3</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSpendBased}
                onChange={(e) => setIncludeSpendBased(e.target.checked)}
                disabled={isSubmitting}
              />
            }
            label='Include spend based transactions'
          />
        </Box>

        <LoadingButton
          variant='contained'
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || (!organisationId && !calendarYear)}
        >
          Regenerate Transactions
        </LoadingButton>
      </Box>
    </PageOuterContainer>
  );
};
